import React, { useState, useEffect, useRef } from "react";
import { bool, number, string } from "prop-types";

const ColumnResizer = ({
  className = "",
  onResizeStart = () => {},
  onResize = (prev, next) => {},
  onResizeStop = (prev, next) => {}
}) => {
  const [dragging, setDragging] = useState(false);
  const [mouseX, setMouseX] = useState(0);
  const [startPos, setStartPos] = useState(0);
  const [startWidthPrev, setStartWidthPrev] = useState(0);
  const [startWidthNext, setStartWidthNext] = useState(0);
  const [clientWidthPrev, setClientWidthPrev] = useState(0);
  const [clientWidthNext, setClientWidthNext] = useState(0);
  const [indexes, setIndexes] = useState(null);

  const ele = useRef(null);

  const endDrag = () => {
    if (!dragging) return;

    setDragging(false);
    onResizeStop();
  };

  const onMouseMove = e => {
    let mouse = e.touches ? e.touches[0].screenX : e.screenX;
    setMouseX(mouse);
    if (!dragging) return;

    const elm = ele.current;

    let moveDiff = mouse - startPos;

    if (moveDiff >= clientWidthNext) {
      moveDiff = clientWidthNext;
    }

    if (moveDiff <= -clientWidthPrev) {
      moveDiff = -clientWidthPrev;
    }

    const newWidthPrev =
      startWidthPrev + (moveDiff * startWidthPrev) / clientWidthPrev;
    const newWidthNext =
      startWidthNext - (moveDiff * startWidthNext) / clientWidthNext;

    elm.previousSibling.style.width = newWidthPrev + "%";
    elm.nextSibling.style.width = newWidthNext + "%";

    let body = elm.parentNode.parentNode;
    const [rowIndex, elmIndex] = indexes;

    [...body.children].forEach((tr, trIndex) => {
      let isElmRow = rowIndex === trIndex;
      [...tr.children].forEach((td, tdIndex) => {
        if (!isElmRow) {
          if (tdIndex === elmIndex - 1) {
            td.style.width = newWidthPrev + "%";
          }
          if (tdIndex === elmIndex + 1) {
            td.style.width = newWidthNext + "%";
          }
        }

        td.dataset.width = td.clientWidth + "px"; //#td.style.width;
      });
    });
    onResize(newWidthPrev, newWidthNext);
  };

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", endDrag);
    document.addEventListener("touchmove", onMouseMove);
    document.addEventListener("touchend", endDrag);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchmove", onMouseMove);
      document.removeEventListener("touchend", endDrag);
    };
  });

  const startDrag = () => {
    setDragging(true);
    setStartPos(mouseX);

    let startPrev = 0;
    let startNext = 0;
    let widthPrev = 0;
    let widthNext = 0;
    let rowIndex = 0;

    const elm = ele.current;

    if (elm) {
      let prevSibling = elm.previousSibling;
      let nextSibling = elm.nextSibling;

      if (prevSibling) {
        startPrev = parseFloat(prevSibling.style.width.replace(/[^0-9.]/, ""));
        widthPrev = prevSibling.clientWidth;

        let elmParent = elm.parentNode;
        let parentParent = elmParent.parentNode;
        rowIndex = [...parentParent.children].indexOf(elmParent);
        let elmIndex = [...elmParent.children].indexOf(elm);
        setIndexes([rowIndex, elmIndex]);
      }

      if (nextSibling) {
        startNext = parseFloat(nextSibling.style.width.replace(/[^0-9.]/, ""));
        widthNext = nextSibling.clientWidth;
      }
    }

    setStartWidthPrev(startPrev);
    setStartWidthNext(startNext);
    setClientWidthPrev(widthPrev);
    setClientWidthNext(widthNext);

    onResizeStart(rowIndex);
  };

  const style = {
    userSelect: "none",
    width: 0,
    cursor: "ew-resize"
  };

  return (
    <td
      ref={ele}
      style={style}
      className={className}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
    />
  );
};

export default ColumnResizer;
